import { CONVERSATION_HREF } from "../lib/site";
import { Body, Em, GOLD, GOLD_DEEP, GoldBtn, HAIR, Hero, IVORY, Kicker, Ornament, Rows, SLATE, SLATE_DIM, Section, Serif, Title } from "../components/ms/kit";
import PageShell from "../components/ms/PageShell";
import InquiryForm from "../components/ms/InquiryForm";

const STATS = [
  { value: "$249M", label: "raised by Canadian emerging managers in 2025, the lowest on record" },
  { value: "$1B", label: "committed over three years through the federal Venture and Growth Capital Catalyst Initiative, starting in fiscal 2026-27, which names new and emerging managers for the first time" },
  { value: "1", label: "national pathway from angel investor to institutionally backed fund manager. That is Groundwork." },
];

const STREAMS = [
  ["Women-focused fund managers", "Across the Prairies and the country"],
  ["Indigenous-led capital vehicles", "In treaty territories and in cities"],
  ["Immigrant founder thesis funds", "In major centres and Atlantic Canada"],
  ["Regional anchor GPs", "From outside Toronto and Montréal"],
];

const DIFFERENTIATORS = [
  ["Canadian LP pitch lab", "Managers get pitch coaching built around what Canadian LPs look for, including fund-of-funds programs, regional funds and family offices writing their first venture cheque. Guest LP sessions and mock investment committee meetings give them practice before the real conversations.", "01"],
  ["Angel network access", "Prairie angel networks are built into the program as a structured part of it. Cohort managers meet angels as potential co-investors throughout the year.", "02"],
  ["Peer cohort", "Sessions run monthly online, with in-person gatherings in rotating cities, starting on the Prairies. Peers who know each other's milestones hold one another to them.", "03"],
];

const FORMATION = [
  ["01 · Thesis", "Your strategy, what sets you apart, your target fund size and the track record that supports it."],
  ["02 · Structure", "The fund vehicle, economics and terms, and the legal, administration and compliance setup."],
  ["03 · LP readiness", "LP targeting, your deck and data room, and practice with guest LPs and mock investment committees."],
  ["04 · First close", "Running the raise, anchor LP conversations, and reporting systems for once capital is in."],
];

const SubKicker = ({ children }) => <div className="text-[11px] tracking-[0.32em] uppercase" style={{ color: GOLD_DEEP }}>{children}</div>;

const INTEREST_FIELDS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email", type: "email" },
  { key: "fund", label: "Fund or firm name", optional: true },
  { key: "city", label: "City and province" },
  { key: "stage", label: "Where you are today", options: ["Active angel", "Building a fund thesis", "Raising a first fund", "Other"], wide: true },
  { key: "thesis", label: "Your thesis, in a sentence or two", type: "textarea", wide: true },
  { key: "share", label: "Anything you would like to share", type: "textarea", optional: true, wide: true },
];

const Interest = () => (
  <Section id="interest" testId="gw-interest" className="py-28 md:py-40" width="max-w-4xl">
    <div className="text-center">
      <SubKicker>V · Future Cohorts</SubKicker>
      <Title className="mt-6">Hand-picked cohorts. <Em>Stay tuned for the next.</Em></Title>
      <Body className="mt-8 max-w-2xl mx-auto">Each Groundwork cohort is hand-picked, and the current cohort is full. If you are building toward a first fund, tell us about yourself and we will be in touch when the next cohort opens.</Body>
    </div>
    <InquiryForm form="groundwork" fields={INTEREST_FIELDS} submitLabel="Express interest" testId="gw-form"
      thanks="Your interest has reached us. We will be in touch when the next Groundwork cohort opens." />
  </Section>
);

const Problem = () => (
  <Section testId="gw-problem" width="max-w-5xl">
    <div className="text-center max-w-3xl mx-auto">
      <SubKicker>I · The Problem</SubKicker>
      <Title className="mt-6">Canada has a gap in its pipeline of <Em>general partners.</Em></Title>
      <Body className="mt-8">Federal capital for emerging fund managers is starting to flow, but the managers who should receive it have not yet had the support to get ready.</Body>
    </div>
    <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="gw-stats">
      {STATS.map((s) => (
        <div key={s.value} className="text-center px-6 py-10" style={{ background: "rgba(255,255,255,0.02)", border: HAIR, borderRadius: 4 }}>
          <div style={{ ...Serif, color: GOLD, fontSize: "clamp(2.75rem, 5vw, 3.75rem)", lineHeight: 1 }}>{s.value}</div>
          <div className="mt-6 text-[13.5px] leading-[1.65]" style={{ color: SLATE }}>{s.label}</div>
        </div>
      ))}
    </div>
    <p className="mt-6 text-center text-[11px] tracking-[0.2em]" style={{ color: SLATE_DIM }}>Sources: RBCx Canadian Venture Capital Report 2025; Government of Canada Budget 2025.</p>
    <p className="mt-14 max-w-3xl mx-auto text-[16.5px] leading-[1.85] text-center" style={{ color: IVORY }}>
      Institutional investors need a track record before they will commit. The managers who will meet that bar in three years are in angel networks today, and until now there has been no clear route from there to here. <Em>Groundwork is meant to be that route.</Em>
    </p>
  </Section>
);

const ProgramBlock = () => (
  <Section testId="gw-program" width="max-w-5xl">
    <div className="text-center max-w-3xl mx-auto">
      <SubKicker>II · The Program</SubKicker>
      <Title className="mt-6">A national cohort for <Em>pre-emerging</Em> fund managers.</Title>
      <Body className="mt-8">Groundwork is a peer cohort with real accountability. It gives managers Canadian LP context, access to angel networks, and a group of peers who are building alongside them. Each cohort has eight to twelve managers, chosen for the strength of their thesis and for geographic spread, including:</Body>
    </div>
    <div className="mt-14 max-w-3xl mx-auto" data-testid="gw-streams">
      {STREAMS.map(([who, where], i) => (
        <div key={who} className="grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-3 md:gap-8 py-6" style={{ borderTop: i === 0 ? HAIR : "none", borderBottom: HAIR }}>
          <div style={{ ...Serif, color: IVORY, fontSize: "clamp(1.15rem, 1.9vw, 1.4rem)" }}>{who}</div>
          <div className="text-[11px] tracking-[0.24em] uppercase md:text-right self-center" style={{ color: GOLD_DEEP }}>{where}</div>
        </div>
      ))}
    </div>
  </Section>
);

const Formation = () => (
  <Section testId="gw-formation" width="max-w-5xl">
    <div className="text-center max-w-3xl mx-auto">
      <SubKicker>IV · Fund Formation Advisory</SubKicker>
      <Title className="mt-6">From thesis to <Em>first close.</Em></Title>
      <Body className="mt-8">For emerging venture capital and private equity managers who are ready to raise, Maven Skies works alongside you at every step of forming a fund. We draw on Venture University&apos;s programs, the CVCA&apos;s national network and trusted legal partners, so you are not building the infrastructure alone. This is available to Groundwork participants and by engagement.</Body>
    </div>
    <Rows items={FORMATION} testId="gw-formation-steps" />
    <p className="mt-6 text-[12px]" style={{ color: SLATE_DIM }}>Legal and tax advice is provided by independent counsel.</p>
  </Section>
);

export default function Groundwork() {
  return (
    <PageShell testId="groundwork-page" disclaimer>
      <Hero kicker="Groundwork by Maven Skies" testId="gw"
        extra={<>
          <Body className="mt-10 max-w-2xl mx-auto" tone={IVORY}>Canada&apos;s next generation of fund managers is already out there, mostly inside angel networks, and we want to help them become institutionally backed managers. Groundwork is a structured pathway that takes an angel investor through to a first fund.</Body>
          <Ornament />
          <p className="text-[11px] tracking-[0.3em] uppercase leading-[1.9]" style={{ color: GOLD_DEEP }}>Maven Skies Inc. and Capital M Ventures, in partnership with the CVCA and Venture University</p>
        </>}>
        Canada&apos;s national program for <Em>emerging fund managers.</Em>
      </Hero>
      <Problem />
      <ProgramBlock />
      <Section testId="gw-differentiation" width="max-w-5xl">
        <div className="text-center max-w-3xl mx-auto">
          <SubKicker>III · What Makes It Different</SubKicker>
          <Title className="mt-6">What makes it <Em>different.</Em></Title>
        </div>
        <Rows items={DIFFERENTIATORS} testId="gw-differentiators" labelWidth="md:grid-cols-[320px_1fr]" />
      </Section>
      <Formation />
      <Interest />
      <Section testId="gw-closing" className="py-32 md:py-44" width="max-w-3xl">
        <div className="text-center">
          <Kicker>The Case</Kicker>
          <p className="mt-10 leading-[1.35]" style={{ ...Serif, color: IVORY, fontSize: "clamp(1.6rem, 3.2vw, 2.4rem)", fontStyle: "italic" }}>
            Canada&apos;s next generation of fund managers will emerge faster if someone builds the infrastructure, brings the peer cohort together and opens the LP room.
          </p>
          <p className="mt-10 leading-[1.1]" style={{ ...Serif, color: GOLD, fontSize: "clamp(1.5rem, 2.6vw, 2.1rem)", fontStyle: "italic" }}>Groundwork is that infrastructure.</p>
          <Ornament />
          <div className="mt-4"><GoldBtn href={CONVERSATION_HREF} testId="gw-cta">Start a Conversation</GoldBtn></div>
        </div>
      </Section>
    </PageShell>
  );
}
